import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Drawer, Table, TextInput, Select, DatePicker, TimeField, Edit01, Trash01, Plus, MarkerPin01 } from "@opendate-app/setlist";
import { WizardCard } from "../ClassesShared/WizardShell";
import AvatarStack from "../ClassesShared/AvatarStack";
import useFocusAfterRemove from "../ClassesShared/useFocusAfterRemove";
import { toDate, toValue, parseDateInput, endsNextDay, needsPastStartWarning } from "./dateValue";
import { formatDayLabel, formatClock } from "./scheduleLabels";

export function sessionInstructorOptions(instructors, session) {
  const assigned = [...(session.loaded_instructor_ids || []), ...(session.instructor_ids || [])];
  return instructors
    .filter((instructor) => !instructor.archived || assigned.includes(instructor.id))
    .map((instructor) => ({ value: instructor.id, label: instructor.archived ? `${instructor.name} (archived)` : instructor.name }));
}

function SessionEditor({ session, rooms, instructors, onChange }) {
  const roomOptions = rooms.map((r) => ({ value: String(r.id), label: r.name }));
  return (
    <div className="class-session-editor">
      <TextInput
        label="Session name"
        placeholder="e.g. Day 1 — Intro"
        value={session.name}
        onChange={(e) => onChange(session.key, "name", e.target.value)}
      />
      <div className="class-session-editor__when">
        <DatePicker
          label="Date"
          placeholder="Select date"
          parseDate={parseDateInput}
          value={toDate(session.date)}
          onChange={(date) => onChange(session.key, "date", toValue(date))}
        />
        <TimeField label="Start" value={session.start || ""} onChange={(v) => onChange(session.key, "start", v)} />
        <TimeField
          label="End"
          hint={endsNextDay(session.start, session.end) ? "Ends the next day" : undefined}
          value={session.end || ""}
          onChange={(v) => onChange(session.key, "end", v)}
        />
      </div>
      <div className="class-session-editor__who">
        <Select
          label="Room"
          placeholder="No room"
          searchable
          clearable
          options={roomOptions}
          value={session.room_id ? String(session.room_id) : null}
          onChange={(v) => onChange(session.key, "room_id", v || null)}
        />
        <Select
          label="Instructors"
          multiple
          searchable
          placeholder="Assign instructors…"
          options={sessionInstructorOptions(instructors, session)}
          value={session.instructor_ids || []}
          onChange={(vals) => onChange(session.key, "instructor_ids", vals || [])}
        />
      </div>
    </div>
  );
}

SessionEditor.propTypes = {
  session: PropTypes.object.isRequired,
  rooms: PropTypes.array.isRequired,
  instructors: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

function sessionColumns({ rooms, instructors, timeZone, trustSavedSessions, sessions, onEdit, onRemove }) {
  const notSet = <span className="class-session-table__unset">Not set</span>;
  const positions = new Map(sessions.map((session, index) => [session.key, index]));
  const indexOf = (session) => positions.get(session.key);
  return [
    {
      key: "name",
      header: "Session",
      primary: true,
      render: (session) => session.name || <span className="class-session-table__unset">{`Session ${indexOf(session) + 1}`}</span>,
    },
    {
      key: "date",
      header: "Date",
      render: (session) => (
        <>
          {session.date ? formatDayLabel(session.date) : notSet}
          {needsPastStartWarning(session, timeZone, new Date(), trustSavedSessions) && <span className="class-session-table__flag">In the past</span>}
        </>
      ),
    },
    { key: "start", header: "Start", render: (session) => (session.start ? formatClock(session.start) : notSet) },
    {
      key: "end",
      header: "End",
      render: (session) => (
        <>
          {session.end ? formatClock(session.end) : notSet}
          {endsNextDay(session.start, session.end) && <span className="class-session-table__flag">Next day</span>}
        </>
      ),
    },
    {
      key: "who",
      header: "Room & instructors",
      render: (session) => {
        const room = rooms.find((r) => String(r.id) === String(session.room_id));
        const assigned = (session.instructor_ids || [])
          .map((id) => instructors.find((instructor) => instructor.id === id))
          .filter(Boolean)
          .map((instructor) => ({ id: instructor.id, name: instructor.name, src: instructor.photo_url }));
        if (!room && assigned.length === 0) return <span className="class-session-table__unset">—</span>;
        return (
          <div className="class-session-table__who">
            {room && (
              <span className="class-session-table__room" title={room.name}>
                <MarkerPin01 width={14} height={14} aria-hidden="true" />
                {room.name}
              </span>
            )}
            {assigned.length > 0 && <AvatarStack people={assigned} size="xs" />}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (session) => (
        <div className="class-session-table__actions">
          <Button variant="tertiary" size="sm" icon={Edit01} aria-label={`Edit session ${indexOf(session) + 1}`} data-field={`session-${session.key}`} onClick={() => onEdit(session.key)} />
          <span className="classes-danger-icon">
            <Button variant="tertiary" size="sm" icon={Trash01} aria-label={`Remove session ${indexOf(session) + 1}`} data-session-remove onClick={() => onRemove(session.key, indexOf(session))} />
          </span>
        </div>
      ),
    },
  ];
}

export function scheduleFooterText(sessions) {
  const count = sessions.length;
  const plural = count === 1 ? "" : "s";
  const scheduled = sessions.filter((s) => s.date && s.start && s.end).length;
  if (scheduled === count) return `${count} session${plural} scheduled. Fine-tune any session, including its room and instructors, by editing the row.`;
  if (scheduled === 0) return `${count} session${plural} inherited from the template. Set your date range and class times above, then Generate Schedule, or set each date by editing the row.`;
  return `${scheduled} of ${count} sessions scheduled. Set the rest with Generate Schedule, or by editing each row.`;
}

function SessionList({ sessions, rooms, instructors, timeZone, trustSavedSessions, hint, confirmClear, onChange, onAdd, onRemove, onClear }) {
  const [editingKey, setEditingKey] = useState(null);
  const focusAfterRemove = useFocusAfterRemove();
  const editingIndex = sessions.findIndex((session) => session.key === editingKey);
  const editingSession = editingIndex === -1 ? null : sessions[editingIndex];
  const add = () => {
    const key = onAdd();
    if (key) setEditingKey(key);
  };
  const remove = (key, index) => {
    if (key === editingKey) setEditingKey(null);
    focusAfterRemove("[data-session-remove]", index, "[data-field=\"add-session\"]");
    onRemove(key);
  };
  const clear = () => {
    if (confirmClear && !window.confirm(`Remove all ${sessions.length} session${sessions.length === 1 ? "" : "s"} from this class? Nothing changes until you save.`)) return;
    setEditingKey(null);
    onClear();
  };
  const columns = sessionColumns({ rooms, instructors, timeZone, trustSavedSessions, sessions, onEdit: setEditingKey, onRemove: remove });

  return (
    <WizardCard
      title="Generated Schedule"
      padding="none"
      actions={(
        <div className="d-flex align-items-center" style={{ gap: 16 }}>
          <Button variant="link-color" size="sm" icon={Plus} data-field="add-session" onClick={add}>Add Date</Button>
          {sessions.length > 0 && <Button variant="link-gray" size="sm" onClick={clear}>Clear All</Button>}
        </div>
      )}
    >
      {sessions.length === 0 ? (
        <div className="class-session-table__empty">{hint}</div>
      ) : (
        <Table
          className="classes-table"
          columns={columns}
          data={sessions}
          getRowKey={(session) => session.key}
          stackBelow={640}
        />
      )}
      <div className={sessions.length > 0 ? "class-session-table__footer" : undefined} role="status">
        {sessions.length > 0 && scheduleFooterText(sessions)}
      </div>
      <Drawer
        open={Boolean(editingSession)}
        onClose={() => setEditingKey(null)}
        title={editingSession ? `Edit session ${editingIndex + 1}` : ""}
        footer={<Button variant="primary" onClick={() => setEditingKey(null)}>Done</Button>}
      >
        {editingSession && (
          <SessionEditor session={editingSession} rooms={rooms} instructors={instructors} onChange={onChange} />
        )}
      </Drawer>
    </WizardCard>
  );
}

SessionList.propTypes = {
  sessions: PropTypes.array.isRequired,
  rooms: PropTypes.array,
  instructors: PropTypes.array,
  timeZone: PropTypes.string,
  trustSavedSessions: PropTypes.bool,
  hint: PropTypes.string,
  confirmClear: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

SessionList.defaultProps = {
  rooms: [],
  instructors: [],
  timeZone: null,
  trustSavedSessions: false,
  confirmClear: false,
  hint: "Set a date range and class times above, then click Generate Schedule.",
};

export default SessionList;
